import { defineStore } from 'pinia'
import { datasetApi, revenueApi } from '../api/dataset.js'

/**
 * データセット管理ストア
 */
export const useDatasetStore = defineStore('dataset', {
  state: () => ({
    // データセット一覧
    datasets: [],
    datasetsLoading: false,
    datasetsError: null,

    // 選択中のデータセット
    currentDataset: null,
    currentDatasetLoading: false,
    currentDatasetError: null,

    // 収益レポート
    providerRevenue: null,
    providerRevenueLoading: false,
    providerRevenueError: null,

    datasetRevenue: null,
    datasetRevenueLoading: false,
    datasetRevenueError: null,

    platformRevenue: null,
    platformRevenueLoading: false,
    platformRevenueError: null,
  }),

  getters: {
    /**
     * 有料データセットのみ
     */
    paidDatasets: (state) => {
      return state.datasets.filter((d) => d.price_per_search > 0)
    },

    /**
     * 無料データセットのみ
     */
    freeDatasets: (state) => {
      return state.datasets.filter((d) => d.price_per_search === 0)
    },

    /**
     * プラットフォーム提供データセット
     */
    platformDatasets: (state) => {
      return state.datasets.filter((d) => d.is_platform_dataset)
    },

    /**
     * データプロバイダー提供データセット
     */
    providerDatasets: (state) => {
      return state.datasets.filter((d) => !d.is_platform_dataset)
    },
  },

  actions: {
    /**
     * プラットフォーム管理者が公開データセットを作成・割り当て
     */
    async assignDataset(data) {
      try {
        const result = await datasetApi.assignDataset(data)
        // リストに追加
        await this.fetchDatasets()
        return result
      } catch (error) {
        console.error('Failed to assign dataset:', error)
        throw error
      }
    },

    /**
     * データプロバイダーが公開データセットを作成
     */
    async createDataset(data) {
      try {
        const result = await datasetApi.createDataset(data)
        // リストに追加
        await this.fetchDatasets()
        return result
      } catch (error) {
        console.error('Failed to create dataset:', error)
        throw error
      }
    },

    /**
     * データセット一覧取得
     */
    async fetchDatasets(params = {}) {
      this.datasetsLoading = true
      this.datasetsError = null

      try {
        const result = await datasetApi.listDatasets(params)
        this.datasets = result.data.datasets
        return result
      } catch (error) {
        this.datasetsError = error.response?.data?.error?.message || 'データセット一覧の取得に失敗しました'
        console.error('Failed to fetch datasets:', error)
        throw error
      } finally {
        this.datasetsLoading = false
      }
    },

    /**
     * データセット詳細取得
     */
    async fetchDataset(datasetId) {
      this.currentDatasetLoading = true
      this.currentDatasetError = null

      try {
        const result = await datasetApi.getDataset(datasetId)
        this.currentDataset = result.data
        return result
      } catch (error) {
        this.currentDatasetError = error.response?.data?.error?.message || 'データセット詳細の取得に失敗しました'
        console.error('Failed to fetch dataset:', error)
        throw error
      } finally {
        this.currentDatasetLoading = false
      }
    },

    /**
     * データセット価格変更
     */
    async changePrice(datasetId, newPrice, reason) {
      try {
        const result = await datasetApi.changePrice(datasetId, {
          new_price: newPrice,
          reason,
        })

        // 現在のデータセットを更新
        if (this.currentDataset && this.currentDataset.dataset_id === datasetId) {
          this.currentDataset.price_per_search = newPrice
        }

        // リストを更新
        const index = this.datasets.findIndex((d) => d.dataset_id === datasetId)
        if (index !== -1) {
          this.datasets[index].price_per_search = newPrice
        }

        return result
      } catch (error) {
        console.error('Failed to change price:', error)
        throw error
      }
    },

    /**
     * データセット削除
     */
    async deleteDataset(datasetId) {
      try {
        const result = await datasetApi.deleteDataset(datasetId)

        // リストから削除
        this.datasets = this.datasets.filter((d) => d.dataset_id !== datasetId)

        // 現在のデータセットをクリア
        if (this.currentDataset && this.currentDataset.dataset_id === datasetId) {
          this.currentDataset = null
        }

        return result
      } catch (error) {
        console.error('Failed to delete dataset:', error)
        throw error
      }
    },

    /**
     * データプロバイダーの月次収益取得
     */
    async fetchProviderRevenue(yearMonth) {
      this.providerRevenueLoading = true
      this.providerRevenueError = null

      try {
        const result = await revenueApi.getProviderMonthlyRevenue(yearMonth)
        this.providerRevenue = result.data
        return result
      } catch (error) {
        this.providerRevenueError = error.response?.data?.error?.message || '収益データの取得に失敗しました'
        console.error('Failed to fetch provider revenue:', error)
        throw error
      } finally {
        this.providerRevenueLoading = false
      }
    },

    /**
     * 特定データセットの月次収益取得
     */
    async fetchDatasetRevenue(datasetId, yearMonth) {
      this.datasetRevenueLoading = true
      this.datasetRevenueError = null

      try {
        const result = await revenueApi.getDatasetMonthlyRevenue(datasetId, yearMonth)
        this.datasetRevenue = result.data
        return result
      } catch (error) {
        this.datasetRevenueError = error.response?.data?.error?.message || '収益データの取得に失敗しました'
        console.error('Failed to fetch dataset revenue:', error)
        throw error
      } finally {
        this.datasetRevenueLoading = false
      }
    },

    /**
     * プラットフォーム全体の月次収益取得
     */
    async fetchPlatformRevenue(yearMonth) {
      this.platformRevenueLoading = true
      this.platformRevenueError = null

      try {
        const result = await revenueApi.getPlatformRevenueSummary(yearMonth)
        this.platformRevenue = result.data
        return result
      } catch (error) {
        this.platformRevenueError = error.response?.data?.error?.message || '収益データの取得に失敗しました'
        console.error('Failed to fetch platform revenue:', error)
        throw error
      } finally {
        this.platformRevenueLoading = false
      }
    },

    /**
     * ストアをリセット
     */
    reset() {
      this.datasets = []
      this.currentDataset = null
      this.providerRevenue = null
      this.datasetRevenue = null
      this.platformRevenue = null
    },
  },
})
